// src/components/DeparturesTab.jsx
import React, { useState, useEffect, useRef } from "react";
import { Spinner } from 'react-bootstrap';
import { FaPlaneDeparture } from 'react-icons/fa';
import axios from "axios";
import Swal from "sweetalert2";
import CryptoJS from "crypto-js"; // Import CryptoJS for encryption
import generateInvoice from "../invoices/frontOffice"; // Import the Invoice component
import { Link } from "react-router-dom";
import { renderDate } from "../utils/dateFormatter";

const DeparturesTab = ({ businessDate }) => {
  const [departureData, setDepartureData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const tabRef = useRef(null);
  const secretKey = "abde100199199111";

  // Define apiURL and token directly within the component
  const apiURL = process.env.REACT_APP_API_URL || "https://api.example.com";
  const token = localStorage.getItem("token");

  // Placeholder for the generateInvoice function (replace with your actual implementation)


  const handleCheckOut = async (guest, index) => {
    Swal.fire({
      title: `Proceed with Check-out?`,
      text: `Do you want to check out ${guest.name}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Check-out!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      try {
        // Show loader while processing
        Swal.fire({
          title: "Processing Check-out...",
          text: "Please wait...",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        // Create request payload
        const requestData = {
          bookingId: guest.booking_id,
          guestName: guest.name,
          phone: guest.phone,
          email: guest.email,
          nationality: guest.nationality,
          checkInDate: guest.checkIn,
          roomNumber: guest.roomNumber,
          roomType: guest.roomType,
          nights: guest.nights,
          reservationType: guest.reservationType,
          bookingSource: guest.bookingSource,
        };

        // Call Check-out API
        const response = await axios.post(
          `${apiURL}/booking/guest-checkout`,
          requestData,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.status === 200) {
          Swal.fire({
            title: "Check-out Successful!",
            text: response.data.message || `${guest.name} has been checked out.`,
            icon: "success",
          }).then(async () => {
            // Generate Invoice
       
            const pdfBlob = await generateInvoice(response.data.invoice.invoice_id,"invoice");
            if (!(pdfBlob instanceof Blob)) {
              throw new Error("Generated invoice is not a valid Blob.");
            }
  
            const pdfUrl = URL.createObjectURL(pdfBlob);
  
            // ✅ Open PDF automatically in a new tab
            window.open(pdfUrl, "_blank");
  
            // Remove the guest from the table after successful check-out
            setDepartureData((prevData) => prevData.filter((_, i) => i !== index));
          });
        } else {
          throw new Error("Unexpected response from API");
        }
      } catch (error) {
        console.error("Check-out Error:", error);
        const errorMessage = error.response?.data?.message || "An error occurred during check-out.";
        Swal.fire({
          title: "Check-out Failed!",
          text: errorMessage,
          icon: "error",
        });
      }
    });
  };

  useEffect(() => {
    let observer;

    const loadDepartureData = async () => {
      setIsLoading(true); // Set loading to true before fetching
      try {
        console.log('Attempting to fetch departure reservations for date:', businessDate);
        const response = await fetch(`${apiURL}/booking/reservations?depadate=${businessDate}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        // Transform the API response to match the structure
        const transformedData = data.reservations.map(reservation => ({
          name: reservation.guest_name,
          phone: reservation.guest_phone,
          email: reservation.guest_email, // Added for check-out
          nationality: reservation.guest_nationality, // Added for check-out
          accommodation: `${reservation.room_types} - ${reservation.room_numbers}`,
          stay: {
            nights: Math.ceil((new Date(reservation.check_out_date) - new Date(reservation.check_in_date)) / (1000 * 60 * 60 * 24)),
            adults: reservation.num_adults || 1, // Use actual data if available
            children: reservation.num_children || 0, // Use actual data if available
          },
          checkIn: renderDate(reservation.check_in_date),
          checkOut: renderDate(reservation.check_out_date),
          booking_id: reservation.booking_id,
          booking_status: reservation.booking_status,
          status: reservation.booking_status === 'reserved' ? 'Ready' :
                  reservation.booking_status === 'cancelled' ? 'Cancelled' :
                  reservation.booking_status === 'checked_in' ? 'Checked-in' :
                  'Checked-out',
          roomNumber: reservation.room_numbers, // Added for check-out
          roomType: reservation.room_types, // Added for check-out
          nights: Math.ceil((new Date(reservation.check_out_date) - new Date(reservation.check_in_date)) / (1000 * 60 * 60 * 24)), // Added for check-out
          reservationType: reservation.reservation_type, // Added for check-out
          bookingSource: reservation.booking_source, // Added for check-out
        }));

        setDepartureData(transformedData);
      } catch (error) {
        console.error('Error fetching departure reservations:', error);
        setDepartureData([]); // Fallback to empty array on error
      } finally {
        setIsLoading(false); // Set loading to false after fetching
      }
    };

    // Lazy loading with Intersection Observer
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        console.log('Departures tab section is intersecting, triggering departure API call');
        loadDepartureData();
        observer.unobserve(tabRef.current);
      }
    }, options);

    if (tabRef.current) {
      observer.observe(tabRef.current);
    } else {
      console.warn('tabRef.current is null, retrying in 1 second for departures...');
      setTimeout(() => observer.observe(tabRef.current), 1000);
    }

    // Fallback: Call the API after 2 seconds if IntersectionObserver doesn’t trigger
    const fallbackTimer = setTimeout(() => {
      if (isLoading) {
        console.log('Falling back to direct departure API call due to no intersection');
        loadDepartureData();
      }
    }, 2000);

    return () => {
      if (observer && tabRef.current) {
        observer.unobserve(tabRef.current);
      }
      clearTimeout(fallbackTimer);
    };
  }, [apiURL, token, businessDate]);

  return (
    <div ref={tabRef}>
      {isLoading ? (
        <div className="text-center py-4">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Loading departures data...</p>
        </div>
      ) : departureData.length > 0 ? (
        <table className="table">
        <thead>
          <tr>
            <th>Guest</th>
            <th>Accommodation</th>
            <th>Stay</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {departureData.map((guest, index) => {
            // Encrypt the booking_id
            const encryptedId = CryptoJS.AES.encrypt(
              guest.booking_id.toString(),
              secretKey
            ).toString();

            return (
              <tr key={index}>
                <td>
                  <Link
                    to={`/hospitality/reservation-details/${encodeURIComponent(encryptedId)}`}
                    className="colored-box reservation-no-box" style={{ minWidth: "0px" }}
                  >
                    {guest.name}
                  </Link>
                </td>
                <td>{guest.accommodation}</td>
                <td>
                  {guest.checkIn} → {guest.checkOut}
                  <p>
                    {guest.stay.nights} Night(s), {guest.stay.adults} Adult(s),{" "}
                    {guest.stay.children} Child(ren)
                  </p>
                </td>
                <td>
                  {guest.booking_status === "checked_in" ? (
                    <button
                      className="btn bg-danger"
                      style={{
                        display: "inline-block",
                        padding: "4px 12px",
                        borderRadius: "4px",
                        color: "#fff",
                        fontSize: "12px",
                        fontWeight: "500",
                        marginTop: "8px",
                        textTransform: "capitalize",
                      }}
                      onClick={() => handleCheckOut(guest, index)}
                    >
                      Check-out
                    </button>
                  ) : (
                    <span>{guest.status}</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      ) : (
        <div className="text-center py-4 text-muted">
          <FaPlaneDeparture size={30} className="mb-2" />
          <p>No Departures Available</p>
        </div>
      )}
    </div>
  );
};

export default DeparturesTab;